import type { BoxProps } from '@chakra-ui/react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { ProductLineItemPrice } from 'components/product/ProductLineItem/ProductLineItemPrice';
import { useProductsUpdatedPrices } from 'hooks/product/useProductsUpdatedPrices';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import { useIsSkincare } from 'hooks/useIsSkincare';
import NextLink from 'next/link';
import { type Ref } from 'react';
import type { PromoCode } from 'types/cart/PromoCode';
import type { ProductComparison } from 'types/cms/blocks';

interface ProductCTAProps extends BoxProps {
  href: string;
  slug: string;
  promoCode?: PromoCode;
  ctaType: ProductComparison['ctaType'];
  ref?: Ref<HTMLDivElement>;
}

export const ProductInfo = ({
  href,
  slug,
  promoCode,
  ctaType,
  ref,
  ...props
}: ProductCTAProps) => {
  const isSkincare = useIsSkincare();
  const { data: shopifyProduct } = useShopifyProduct(slug);
  const { data: updatedProducts } = useProductsUpdatedPrices({
    blockId: slug,
    shopifyProducts: shopifyProduct ? [shopifyProduct] : null,
    promoCodes: promoCode ? [promoCode] : undefined,
  });

  const updatedProduct = updatedProducts?.[0];

  const title = shopifyProduct?.title;
  const netContents = shopifyProduct?.variants[0].netContents;
  const price = updatedProduct?.variants[0].price;
  const compareAtPrice = updatedProduct?.variants[0].compareAtPrice;

  const CONTAINER_BREAKPOINT = compareAtPrice ? '200px' : '170px';

  return (
    <Box
      ref={ref}
      display="flex"
      flexDir="column"
      flexGrow={1}
      mb="2xs"
      sx={{
        containerType: 'inline-size',
      }}
      {...props}
    >
      {title && (
        <NextLink href={href}>
          <Text as="h5" size="title2">
            {title}
          </Text>
        </NextLink>
      )}
      {((ctaType === 'atc' && price) || (isSkincare && netContents)) && (
        <HStack
          as="span"
          mt="auto"
          pt="4xs"
          gap="4xs"
          rowGap={0}
          flexDirection="column"
          alignItems="start"
          sx={{
            [`@container (min-width: ${CONTAINER_BREAKPOINT})`]: {
              '&': {
                flexDirection: 'row',
              },
            },
          }}
        >
          {ctaType === 'atc' && price && (
            <ProductLineItemPrice
              textProps={{
                size: 'detail',
                fontWeight: 'medium',
                sx: { fontVariantNumeric: 'slashed-zero' },
              }}
              ml={0}
              gap="5xs"
              availableForSale
              price={price.amount}
              compareAtPrice={compareAtPrice?.amount}
            />
          )}
          {isSkincare && netContents && (
            <Text
              as="span"
              size="detail"
              fontWeight="book"
              color="blackAlpha.700"
              sx={{
                fontVariantNumeric: 'slashed-zero',
              }}
            >
              {netContents}
            </Text>
          )}
        </HStack>
      )}
    </Box>
  );
};
